import {
  ReportDailyResultImg,
  RiskLabel,
} from "@/pages/common/report/WebReport2/commons";
import { SLEEP_JUDGE_TITLE_CONFIG } from "@/pages/common/report/WebReport2/SleepJudge/commons";
import { NavigateNext } from "@mui/icons-material";
import {
  Breadcrumbs,
  Divider,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";
import { differenceInCalendarDays, format, parseISO } from "date-fns";
import Highcharts, {
  SeriesLegendItemClickEventObject,
  YAxisPlotLinesOptions,
} from "highcharts";
import HighchartsReact from "highcharts-react-official";
import "highcharts/highcharts-more";
import { useRef } from "react";
import ReportNavigate from "./commons/ReportNavigate";
import { ScrollableContainer } from "./commons/ScrollableContainer";
import SubtitleTypography from "./commons/SubtitleTypography";
import { Rhythm2Response, RhythmMeasurementResults, UserType } from "./types";

export default function Rhythm2({
  uuid,
  userType,
  data,
}: {
  uuid: string | undefined;
  userType: UserType;
  data: Rhythm2Response;
}) {
  return (
    <Stack sx={{ px: 6 }}>
      <Stack sx={{ pt: 12, pb: 8, gap: 2 }}>
        <Breadcrumbs maxItems={2} separator={<NavigateNext fontSize="small" />}>
          <Link
            href={`/${userType}/report/${uuid}`}
            color="text.secondary"
            style={{ textDecoration: "none" }}
          >
            <Typography variant="body2">レポートTOP</Typography>
          </Link>
          <Typography variant="body2">睡眠のリズム</Typography>
        </Breadcrumbs>
        <Typography
          variant="h5"
          sx={{ textAlign: "justify", color: "primary.main" }}
        >
          睡眠のリズム
        </Typography>
      </Stack>
      <Divider />
      <Stack sx={{ pt: "49px", pb: 6 }}>
        <SubtitleTypography>日別の測定結果と判定</SubtitleTypography>
      </Stack>
      <Stack sx={{ pb: 12, gap: 4 }}>
        <Stack gap={4} sx={{ mb: 4 }}>
          <Stack sx={{ textAlign: "right" }}>
            <Typography
              variant="caption"
              sx={{ "&:before": { content: "'■'", color: "#00A99D", pr: 1 } }}
            >
              推奨起床時間帯
            </Typography>
          </Stack>
          <Chart data={data.measurement_results} />
        </Stack>
        <Stack gap={4}>
          <table style={{ width: "100%" }}>
            <tbody>
              <tr>
                <td style={{ whiteSpace: "nowrap" }}>
                  <Typography
                    variant="body2"
                    sx={{
                      "&::before": {
                        content: "'・'",
                        color: "text.secondary",
                        mr: 2,
                      },
                    }}
                  >
                    あなたの平均的な起床時刻
                  </Typography>
                </td>
                <td width="100%">
                  <Divider
                    sx={{
                      mx: 2,
                      border: "none",
                      borderTop: "2px dashed #E0E0E0",
                      "&:before": {
                        content: "''",
                        position: "absolute",
                        left: 0,
                        top: "50%",
                        width: "100%",
                        height: "1px",
                      },
                    }}
                  />
                </td>
                <td style={{ whiteSpace: "nowrap" }}>
                  <Typography
                    fontWeight={500}
                    fontSize="16px"
                    color="text.primary"
                  >
                    {data.measurement_results.average_wake_up_time ?? "-"}
                  </Typography>
                </td>
              </tr>
            </tbody>
          </table>
          <RhythmTable data={data} />
        </Stack>
        <Stack gap={4} sx={{ mb: 4 }}>
          <Typography variant="subtitle1" color="text.secondary">
            日別判定の基準
          </Typography>
          <Stack gap={2}>
            <TableContainer
              sx={{
                border: "solid 1px #D8D7D6",
                borderRadius: 0,
                "& thead tr th": {
                  p: "8px 0",
                  "&:nth-of-type(2)": {
                    borderLeft: "solid 1px #D8D7D6",
                  },
                },
                "& tbody tr td": {
                  p: 2,
                  "&:nth-of-type(2)": {
                    borderLeft: "solid 1px #D8D7D6",
                  },
                },
                "& tbody tr:last-of-type .MuiTableCell-root": {
                  borderBottom: "none",
                },
              }}
            >
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <Typography variant="caption" sx={{ pl: 2 }}>
                        平均的な起床時刻からのズレ
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ textAlign: "center" }}>
                      <Typography variant="caption">判定</Typography>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {["±2時間以内", "±2時間超"].map((x, i) => (
                    <TableRow key={i}>
                      <TableCell>
                        <Typography fontWeight={400} fontSize="13px">
                          {x}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <ReportDailyResultImg
                          type={i ? 1 : 3}
                          style={{ margin: "auto", display: "block" }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Stack>
        </Stack>
      </Stack>
      <Stack sx={{ pb: 6 }}>
        <SubtitleTypography>睡眠のリズム判定</SubtitleTypography>
      </Stack>
      <Stack sx={{ pb: 12 }}>
        <RiskLabel
          type={SLEEP_JUDGE_TITLE_CONFIG[data.result]}
          title={data.result}
          summary={data.summary}
        />
      </Stack>
      <Divider />
      <Stack>
        <Typography variant="h6" sx={{ color: "text.secondary", pt: 8 }}>
          あわせて読みたい
        </Typography>

        <ReportNavigate
          navigates={[
            {
              link: `/${userType}/report/${uuid}/quantity`,
              text: "睡眠の長さ",
            },
            {
              link: `/${userType}/report/${uuid}/quality`,
              text: "睡眠の質",
            },
          ]}
          reportUid={uuid}
        />
      </Stack>
    </Stack>
  );
}

function RhythmTable({ data }: { data: Rhythm2Response }) {
  return (
    <ScrollableContainer>
      <TableContainer
        sx={{
          minWidth: "768px",
          borderRadius: 0,
          border: "solid 1px #D8D7D6",
          mb: 4,
          ml: 6,
          mr: 6,
          "& thead tr th, tr tbody td": {
            whiteSpace: "nowrap",
          },
          "& tbody tr td": {
            py: 4,
            px: 0,
            border: "none",
            "&:nth-of-type(1)": {
              px: 4,
              borderRight: "solid 1px #D8D7D6",
            },
            "&:not(:nth-of-type(-n + 1))": {
              textAlign: "center",
            },
          },
          "& tbody tr:nth-of-type(1) td": {
            borderBottom: "3px double #D8D7D6",
          },
          "& thead tr th": {
            p: "8px 0",
            "&:not(:nth-of-type(1))": {
              width: "83.71px",
              "& .MuiTypography-caption": {
                display: "block",
                textAlign: "center",
              },
            },
            "&:nth-of-type(1)": {
              width: "120px",
              borderRight: "solid 1px #D8D7D6",
              px: "16px",
              "& .MuiTypography-caption": {
                display: "block",
                textAlign: "left",
              },
            },
            "&:not(:nth-of-type(-n + 2))": {
              "& .MuiTypography-caption": {
                borderLeft: "solid 1px #D8D7D6",
              },
            },
          },
        }}
      >
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <Typography variant="caption">項目</Typography>
              </TableCell>
              {Array.from({ length: 7 }).map((_, i) => (
                <TableCell key={i}>
                  <Typography variant="caption">{i + 1}日目</Typography>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell>
                <Typography fontWeight={400} fontSize="13px">
                  起床時刻のズレ
                </Typography>
              </TableCell>
              {data.each_day_rhythm_gap.map((x, i) => (
                <TableCell key={i} sx={{ textAlign: "center" }}>
                  <Typography fontWeight={400} fontSize="13px">
                    {x != null ? x : "-"}
                  </Typography>
                </TableCell>
              ))}
            </TableRow>
            <TableRow>
              <TableCell>
                <Typography fontWeight={400} fontSize="13px">
                  日別判定
                </Typography>
              </TableCell>
              {data.each_day_sleep?.map((x, i) => (
                <TableCell key={i} sx={{ textAlign: "center" }}>
                  {x != null ? (
                    <ReportDailyResultImg
                      type={x}
                      style={{ margin: "auto", display: "block" }}
                    />
                  ) : (
                    <Typography fontWeight={400} fontSize="13px">
                      -
                    </Typography>
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </ScrollableContainer>
  );
}

function Chart(props: { data: RhythmMeasurementResults }) {
  const theme = useTheme();
  const chartComponentRef = useRef<HighchartsReact.RefObject>(null);
  const averageWakeUpTimeValue =
    props.data.average_wake_up_time != null
      ? parseHourMinutes(props.data.average_wake_up_time)
      : null;

  let min = Math.min(
    ...props.data.items
      .filter((item) => item.time_spent_in_bed_from != null)
      .map((item) => {
        const date = parseISO(item.date);
        const fromDate = parseISO(item.time_spent_in_bed_from!);

        // NOTE: 表示する日付と起床の日付が異なる場合、差の日数*24を加算する
        return (
          (fromDate.getHours() +
            Math.abs(differenceInCalendarDays(fromDate, date)) * 24) *
            60 +
          fromDate.getMinutes()
        );
      })
  );
  if (averageWakeUpTimeValue != null) {
    if (averageWakeUpTimeValue >= min) {
      min = Math.min(averageWakeUpTimeValue - 2 * 60, min);
    }
  }

  const max = Math.max(
    averageWakeUpTimeValue == null
      ? 0
      : averageWakeUpTimeValue + 24 * 60 + 2 * 60,
    ...props.data.items
      .filter((item) => item.time_spent_in_bed_to.date != null)
      .map((item) => {
        const date = parseISO(item.date);
        const toDate = parseISO(item.time_spent_in_bed_to.date!);

        // NOTE: 表示する日付と起床の日付が異なる場合、差の日数*24を加算する
        return (
          (toDate.getHours() +
            Math.abs(differenceInCalendarDays(date, toDate)) * 24) *
            60 +
          toDate.getMinutes()
        );
      })
  );

  const plotBands = [];
  const plotLines: YAxisPlotLinesOptions[] = [
    {
      value: 0,
      color: "#e0e0e0",
    },
    {
      value: max,
      color: "#c4c4c4",
      zIndex: 3,
      width: 1,
      label: {
        x: -36,
        y: 5,
        text: formatChartTime(max),
        style: {
          color: "#c4c4c4",
          fontFamily: "Noto Sans JP",
          fontSize: "10px",
        },
      },
    },
    {
      value: min,
      color: "#c4c4c4",
      zIndex: 3,
      width: 1,
      label: {
        x: -36,
        y: 5,
        text: formatChartTime(min),
        style: {
          color: "#c4c4c4",
          fontFamily: "Noto Sans JP",
          fontSize: "10px",
        },
      },
    },
  ];
  if (averageWakeUpTimeValue != null) {
    if (averageWakeUpTimeValue >= min) {
      plotBands.push({
        // NOTE: チャートは最大48時間になる可能性がある為、「0〜24の間」および「24〜48の間」の「0〜24の間」の分の横軸バンド
        color: "#e0f5f3",
        from: averageWakeUpTimeValue - 2 * 60,
        to: averageWakeUpTimeValue + 2 * 60,
      });
      plotLines.push({
        // NOTE: チャートは最大48時間になる可能性がある為、「0〜24の間」および「24〜48の間」の「0〜24の間」の分の横軸線
        value: averageWakeUpTimeValue,
        color: "#00a99d",
        zIndex: 3,
        width: 1,
        label: {
          x: -36,
          y: 5,
          text: formatChartTime(averageWakeUpTimeValue),
          style: {
            color: "#00A99D",
            fontFamily: "Noto Sans JP",
            fontSize: "10px",
          },
        },
      });
    }
    plotBands.push({
      // NOTE: チャートは最大48時間になる可能性がある為、「0〜24の間」および「24〜48の間」の「24〜48の間」の分の横軸バンド
      color: "#e0f5f3",
      from: averageWakeUpTimeValue + 24 * 60 - 2 * 60,
      to: averageWakeUpTimeValue + 24 * 60 + 2 * 60,
    });
    plotLines.push({
      // NOTE: チャートは最大48時間になる可能性がある為、「0〜24の間」および「24〜48の間」の「24〜48の間」の分の横軸線
      value: averageWakeUpTimeValue + 24 * 60,
      color: "#00a99d",
      zIndex: 3,
      width: 1,
      label: {
        x: -36,
        y: 5,
        text: formatChartTime(averageWakeUpTimeValue + 24 * 60),
        style: {
          color: "#00A99D",
          fontFamily: "Noto Sans JP",
          fontSize: "10px",
        },
      },
    });
  }
  const options: Highcharts.Options = {
    chart: {
      type: "columnrange",
      marginLeft: 36,
      marginRight: 0,
    },
    title: {
      text: "",
    },
    credits: {
      enabled: false,
    },
    tooltip: {
      enabled: false,
    },
    xAxis: {
      categories: props.data.items.map((item) => {
        const date = parseISO(item.date);
        return `${format(date, "M/d")}<br>${"日月火水木金土"[date.getDay()]}`;
      }),
      opposite: true,
      lineWidth: 0,
      labels: {
        rotation: 0,
        style: {
          color: theme.palette.text.secondary,
          fontSize: "10px",
        },
      },
    },
    yAxis: {
      reversed: true,
      endOnTick: false,
      title: {
        text: "",
      },
      labels: {
        enabled: false,
      },
      // NOTE: 起床の日付の最大値 + 2時間をグラフの最大値とする
      max: max,
      // NOTE: 就寝の日付の最小値 - 2時間をグラフの最小値とする
      min: min,
      plotBands: plotBands,
      plotLines: plotLines,
      gridLineWidth: 0,
    },
    plotOptions: {
      columnrange: {
        dataLabels: {
          enabled: true,
        },
      },
    },
    series: [
      {
        type: "columnrange",
        showInLegend: false,
        color: "#003C70",
        data: props.data.items.map((item) => {
          if (
            item.time_spent_in_bed_from == null ||
            item.time_spent_in_bed_to.date == null
          ) {
            // 起床、または消灯時間のいずれかが null の場合、グラフを描画しない。
            return {};
          }
          const date = parseISO(item.date);
          const fromDate = parseISO(item.time_spent_in_bed_from);
          const toDate = parseISO(item.time_spent_in_bed_to.date);

          return {
            low:
              (fromDate.getHours() +
                Math.abs(differenceInCalendarDays(date, fromDate)) * 24) *
                60 +
              fromDate.getMinutes(),
            high:
              (toDate.getHours() +
                Math.abs(differenceInCalendarDays(date, toDate)) * 24) *
                60 +
              toDate.getMinutes(),
            isWarning: item.time_spent_in_bed_to.is_warning,
          };
        }),
        dataLabels: {
          enabled: false,
          style: {
            fontWeight: "500",
          },
        },
        events: {
          legendItemClick(e: SeriesLegendItemClickEventObject) {
            e.preventDefault();
          },
        },
      },
    ],
  };

  return (
    <HighchartsReact
      highcharts={Highcharts}
      options={options}
      ref={chartComponentRef}
    />
  );
}

function parseHourMinutes(hourMinutes: string) {
  // NOTE: hh:mm形式(e.g. 8:06)の時間をDate型に変更し、getHours/getMinutesを取得する為、2020/01/01は任意の日付を使用
  const parsedHourMinutes = new Date("2020/01/01 " + hourMinutes);
  return parsedHourMinutes.getHours() * 60 + parsedHourMinutes.getMinutes();
}

function formatChartTime(value: number) {
  const x = value % (24 * 60);
  const hour = Math.floor(x / 60);
  const min = x % 60;
  return `${hour}:${min.toString().padStart(2, "0")}`;
}
